/**
 * THERAPIST NOTES MODEL - Sistema Premium de Apuntes del Terapeuta
 * 
 * Este módulo define el modelo de datos para el servicio premium de
 * almacenamiento y consulta de apuntes del terapeuta.
 * 
 * CARACTERÍSTICAS:
 * - Almacenamiento de apuntes digitalizados (fotos, PDFs, texto)
 * - Indexación semántica para búsqueda inteligente
 * - Fragmentación para referencias precisas
 * - Ontología personal del terapeuta
 * - Métricas de almacenamiento para facturación
 * 
 * IMPORTANTE: Este servicio está diseñado para ser separable y monetizable.
 * Las métricas de almacenamiento permiten facturación por uso.
 * 
 * Colecciones Firestore:
 * - therapist_notes: Apuntes individuales
 * - therapist_note_chunks: Fragmentos indexados (para RAG)
 * - therapist_ontology: Ontología personal
 * - therapist_notes_usage: Métricas de uso para facturación
 */

// ============================================
// TIPOS PARA APUNTES
// ============================================

/**
 * Tipo de fuente del apunte
 */
export type NoteSourceType =
  | 'photo'           // Foto de apuntes manuscritos
  | 'pdf'             // Documento PDF
  | 'text'            // Texto escrito directamente
  | 'youtube'         // Video de YouTube transcrito
  | 'audio'           // Audio transcrito
  | 'document'        // Otro tipo de documento
  | 'notebook';       // Cuaderno multi-página

/**
 * Página individual de un cuaderno (notebook)
 * Cada página tiene su propia imagen y texto OCR
 */
export interface NotebookPage {
  id: string;
  order: number;                 // Orden en el cuaderno (1, 2, 3...)
  imageUrl: string;              // URL de la imagen original
  thumbnailUrl?: string;         // Miniatura para preview
  extractedText: string;         // Texto OCR de esta página
  ocrConfidence: number;         // Confianza del OCR (0-1)
  fileSizeBytes: number;         // Tamaño de la imagen
  addedAt: string;               // Fecha de añadido
}

/**
 * Estado de procesamiento del apunte
 */
export type NoteProcessingStatus =
  | 'pending'         // En cola
  | 'processing'      // Procesando OCR/transcripción
  | 'indexing'        // Indexando para búsqueda
  | 'ready'           // Listo para consultar
  | 'error';          // Error en procesamiento

/**
 * Apunte del terapeuta - Documento principal
 * 
 * Soporta dos modos:
 * 1. Apunte simple: una sola foto/documento (originalFileUrl + extractedText)
 * 2. Cuaderno (notebook): múltiples páginas (pages[] + extractedText = concatenación)
 */
export interface TherapistNote {
  id: string;
  therapistId: string;

  // Contenido
  title: string;
  sourceType: NoteSourceType;
  originalFileUrl?: string;      // URL del archivo original (Storage) - para apuntes simples
  originalFileName?: string;
  extractedText: string;         // Texto extraído (OCR/transcripción) - o concatenación de páginas
  summary?: string;              // Resumen generado por IA

  // ============================================
  // CUADERNO MULTI-PÁGINA (NOTEBOOK)
  // ============================================

  /**
   * Páginas del cuaderno (solo si sourceType === 'notebook')
   * Si pages existe y tiene items, es un cuaderno multi-página
   * El extractedText será la concatenación de todas las páginas
   */
  pages?: NotebookPage[];

  /**
   * Número de páginas (para mostrar en UI sin cargar pages[])
   */
  pagesCount?: number;

  // ============================================
  // ORGANIZACIÓN FLEXIBLE
  // ============================================

  // Carpeta opcional (para jerarquía)
  folderId?: string;

  // Etiquetas del terapeuta (flexible, editable)
  tags: string[];

  // Etiquetas sugeridas por IA (el terapeuta puede aceptar/rechazar)
  aiSuggestedTags?: string[];

  // Categoría principal (ej: "Formación", "Pacientes", "Investigación")
  category?: string;

  // Contexto adicional (para búsqueda)
  patientId?: string;            // Si el apunte es sobre un paciente específico
  patientName?: string;          // Nombre para mostrar (no vinculante)
  courseOrTraining?: string;     // "Master CBT 2024", "Formación EMDR"
  bookOrSource?: string;         // "Libro: Trauma y Cuerpo", "Artículo: ..."

  // Color para visualización (opcional)
  color?: string;

  // ============================================
  // INDEXACIÓN SEMÁNTICA
  // ============================================

  // Tags del Semantic Layer (auto-detectados)
  semanticTags: string[];
  detectedConcepts: Array<{
    conceptId: string;
    confidence: number;
    matches: string[];
  }>;

  // Metadatos de procesamiento
  status: NoteProcessingStatus;
  processingError?: string;
  chunksCount: number;           // Número de fragmentos indexados

  // Métricas de almacenamiento (para facturación)
  fileSizeBytes: number;
  textCharacterCount: number;

  // Timestamps
  createdAt: string;
  updatedAt: string;
  lastAccessedAt?: string;       // Para stats de uso
}

// ============================================
// CATEGORÍAS Y ETIQUETAS PREDEFINIDAS
// ============================================

/**
 * Categorías predefinidas para organización principal
 * El terapeuta puede añadir las suyas
 */
export const PREDEFINED_NOTE_CATEGORIES = [
  'Formación',           // Cursos, masters, workshops
  'Libros',              // Lecturas profesionales
  'Artículos',           // Papers, investigaciones
  'Casos clínicos',      // Apuntes sobre casos (sin identificar paciente)
  'Técnicas',            // Protocolos, técnicas terapéuticas
  'Supervisión',         // Notas de supervisión
  'Personal',            // Reflexiones propias
  'Recursos',            // Material para compartir con pacientes
];

/**
 * Etiquetas predefinidas por temática
 * El terapeuta puede añadir las suyas
 */
export const PREDEFINED_NOTE_TAGS = [
  // Por enfoque terapéutico
  'CBT',
  'EMDR',
  'ACT',
  'Sistémica',
  'Psicodinámica',
  'Humanista',
  'Gestalt',
  'Mindfulness',

  // Por problemática
  'Ansiedad',
  'Depresión',
  'Trauma',
  'Duelo',
  'Adicciones',
  'TCA',
  'TOC',
  'TDAH',
  'TEA',
  'Parejas',
  'Infanto-juvenil',
  'Adultos',

  // Por tipo de contenido
  'Ejercicios',
  'Protocolos',
  'Evaluación',
  'Psicoeducación',
  'Metáforas',
  'Casos',
];

/**
 * Carpeta de apuntes
 */
export interface TherapistNotesFolder {
  id: string;
  therapistId: string;
  name: string;
  parentId?: string;             // Para subcarpetas
  color?: string;
  icon?: string;
  order: number;
  notesCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Configuración de etiquetas del terapeuta
 */
export interface TherapistNotesSettings {
  id: string;                    // = therapistId
  therapistId: string;

  // Categorías personalizadas (se suman a las predefinidas)
  customCategories: string[];

  // Etiquetas personalizadas (se suman a las predefinidas)
  customTags: string[];

  // Categorías/etiquetas ocultadas
  hiddenCategories: string[];
  hiddenTags: string[];

  // Preferencias
  preferences: {
    defaultCategory?: string;
    autoTagEnabled: boolean;     // ¿La IA sugiere tags automáticamente?
    showPredefinedTags: boolean;
    defaultView: 'list' | 'grid' | 'tags';
  };

  createdAt: string;
  updatedAt: string;
}

/**
 * Fragmento de un apunte - Para RAG con referencias
 * 
 * Cada apunte se divide en chunks para:
 * 1. Búsqueda semántica precisa
 * 2. Referencias exactas al responder
 * 3. Evitar alucinaciones de la IA
 */
export interface TherapistNoteChunk {
  id: string;
  noteId: string;                // Referencia al apunte padre
  therapistId: string;           // Para queries eficientes

  // Contenido del fragmento
  content: string;               // Texto del chunk (max ~500 tokens)
  chunkIndex: number;            // Posición en el documento

  // Localización para referencia
  pageNumber?: number;           // Si es PDF
  startLine?: number;            // Si es texto
  endLine?: number;

  // Contexto del apunte padre (desnormalizado para eficiencia)
  noteTitle: string;
  noteCategory?: string;

  // Indexación Semántica
  semanticTags: string[];        // Tags de este chunk específico
  keywords: string[];            // Palabras clave para búsqueda

  // Embedding (para búsqueda vectorial futura)
  embeddingVersion?: string;     // Versión del modelo de embedding
  hasEmbedding: boolean;

  createdAt: string;
}

// ============================================
// ONTOLOGÍA PERSONAL DEL TERAPEUTA
// ============================================

/**
 * Nodo en el mapa mental/ontología personal
 */
export interface OntologyNode {
  id: string;                    // ID único del nodo
  conceptId?: string;            // Concepto del Semantic Layer si mapea
  label: string;                 // Nombre visible
  description?: string;          // Descripción del terapeuta
  color?: string;                // Color en el mapa mental

  // Conexiones
  parentId?: string;             // Nodo padre (para jerarquía)
  relatedNodeIds: string[];      // Nodos relacionados (no jerárquicos)

  // Origen
  derivedFromNoteIds: string[];  // Apuntes de donde se extrajo

  // Métricas
  mentionCount: number;          // Veces mencionado en apuntes
  lastMentionedAt: string;
}

/**
 * Ontología personal completa del terapeuta
 */
export interface TherapistOntology {
  id: string;                    // = therapistId
  therapistId: string;

  // Nodos del mapa mental
  nodes: OntologyNode[];

  // Estadísticas
  totalNodes: number;
  totalConnections: number;
  lastUpdatedAt: string;

  // Configuración del terapeuta
  settings: {
    autoExtractConcepts: boolean;  // Extraer automáticamente de apuntes
    showSemanticLayerMapping: boolean;  // Mostrar mapeo a ontología global
    preferredVisualization: 'tree' | 'graph' | 'list';
  };

  createdAt: string;
  updatedAt: string;
}

// ============================================
// MÉTRICAS DE USO (PARA FACTURACIÓN)
// ============================================

/**
 * Métricas de uso del servicio por terapeuta
 * Se actualiza periódicamente para facturación
 */
export interface TherapistNotesUsage {
  id: string;                    // = therapistId
  therapistId: string;

  // Almacenamiento
  totalNotesCount: number;
  totalChunksCount: number;
  totalStorageBytes: number;
  totalCharacters: number;

  // Uso
  chatQueriesThisMonth: number;
  lastQueryAt?: string;

  // Plan/Límites (para servicio premium)
  plan: 'free_trial' | 'basic' | 'professional' | 'enterprise';
  storageQuotaBytes: number;
  queriesQuotaMonth: number;

  // Billing
  periodStart: string;           // Inicio del período de facturación
  periodEnd: string;

  createdAt: string;
  updatedAt: string;
}

// ============================================
// DTOs PARA OPERACIONES
// ============================================

export interface CreateTherapistNoteDTO {
  therapistId: string;
  title: string;
  sourceType: NoteSourceType;
  extractedText: string;
  originalFileUrl?: string;
  originalFileName?: string;
  summary?: string;
  folderId?: string;
  tags?: string[];
  category?: string;
  fileSizeBytes?: number;

  // Para cuadernos multi-página
  pages?: NotebookPage[];
  pagesCount?: number;
}

/**
 * DTO para añadir una página a un cuaderno existente
 */
export interface AddPageToNotebookDTO {
  noteId: string;
  therapistId: string;
  page: Omit<NotebookPage, 'id' | 'order'>;
}

export interface UpdateTherapistNoteDTO {
  title?: string;
  tags?: string[];
  category?: string;
  folderId?: string;
}

export interface ChatWithNotesRequest {
  therapistId: string;
  query: string;
  sessionId?: string;            // Para mantener contexto
  maxChunks?: number;            // Máximo de fragmentos a considerar
  includeReferences?: boolean;   // Incluir referencias en respuesta
}

export interface ChatWithNotesResponse {
  answer: string;
  confidence: number;
  references: Array<{
    noteId: string;
    noteTitle: string;
    chunkId: string;
    excerpt: string;             // Fragmento citado
    relevanceScore: number;
  }>;
  noRelevantNotesFound: boolean;
  suggestedFollowUp?: string[];
}

// ============================================
// CONFIGURACIÓN DEL SERVICIO
// ============================================

/**
 * Límites por plan
 */
export const NOTES_PLAN_LIMITS: Record<TherapistNotesUsage['plan'], {
  storageBytes: number;
  monthlyQueries: number;
  maxNotesCount: number;
  chunkingEnabled: boolean;
  semanticSearchEnabled: boolean;
  ontologyEnabled: boolean;
}> = {
  free_trial: {
    storageBytes: 50 * 1024 * 1024,     // 50 MB
    monthlyQueries: 50,
    maxNotesCount: 100,
    chunkingEnabled: true,
    semanticSearchEnabled: true,
    ontologyEnabled: false,
  },
  basic: {
    storageBytes: 500 * 1024 * 1024,    // 500 MB
    monthlyQueries: 200,
    maxNotesCount: 100,
    chunkingEnabled: true,
    semanticSearchEnabled: true,
    ontologyEnabled: true,
  },
  professional: {
    storageBytes: 5 * 1024 * 1024 * 1024,  // 5 GB
    monthlyQueries: 1000,
    maxNotesCount: 500,
    chunkingEnabled: true,
    semanticSearchEnabled: true,
    ontologyEnabled: true,
  },
  enterprise: {
    storageBytes: 50 * 1024 * 1024 * 1024, // 50 GB
    monthlyQueries: -1,                     // Ilimitado
    maxNotesCount: -1,                      // Ilimitado
    chunkingEnabled: true,
    semanticSearchEnabled: true,
    ontologyEnabled: true,
  },
};

/**
 * Configuración de chunking para RAG
 */
export const CHUNKING_CONFIG = {
  maxChunkSize: 500,              // ~500 tokens/chunk
  overlapSize: 50,                // Overlap entre chunks
  minChunkSize: 100,              // Mínimo para no crear chunks muy pequeños
  separators: ['\n\n', '\n', '. ', ', '],  // Prioridad de separación
};

